import { useCallback, useEffect, useMemo, useState } from 'react';

import { PostHog } from 'posthog-js';
import { usePostHog } from 'posthog-js/react';
import { useNavigate, useSearchParams } from 'react-router';

import httpInstances from '@/api/axiosInstance';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import useDocumentTitleRename from '@/hooks/useDocumentTitleRename';
import AboutUser from '@/pages/Onboarding/OnboardingForm/Steps/AboutUser';
import DefaultMarket from '@/pages/Onboarding/OnboardingForm/Steps/DefaultMarket';
import Invite from '@/pages/Onboarding/OnboardingForm/Steps/Invite';
import Usage from '@/pages/Onboarding/OnboardingForm/Steps/Usage';
import { DomainConfig, InviteRecipient } from '@/pages/Onboarding/types/recipient.types';
import { useGxTokenFromStorage, setGxTokenToStorage } from '@/services/storage';
import { getMarketsList } from '@/slices/marketsListSlice';
import { createUser, resetStatus, User } from '@/slices/userCreationSlice';
import { generateSecureRandomString } from '@/utils/generateSecureRandomString';

export const EMAIL_PARAM = 'email';

// Constants
const RECAPTCHA_REFRESH_INTERVAL = 30000; // 30 seconds
const RECAPTCHA_TIMEOUT = 60000; // 60 seconds

function capturePostHogEvent(
  postHog: PostHog | undefined,
  createUserObject: () => User,
  inviteRecipients: InviteRecipient[]
) {
  if (!postHog) return;

  const user = createUserObject();
  const validInvites = inviteRecipients
    .filter(recipient => recipient.email?.trim())
    .map(recipient => recipient.email);

  postHog.alias(user.email);
  postHog.identify(user.email, {
    email: user.email,
    first_name: user.hs_firstname,
    last_name: user.hs_lastname,
    company: user.hs_company,
    preferredMarket: user.prefMarket,
    usage: user.gxgo_declared_data_usage
  });
  postHog.capture('gxgo_onboarding_submitted', {
    user,
    inviteRecipients: validInvites
  });
}

const useOnboardingForm = (domainConfig: DomainConfig | null, verificationToken: string) => {
  const userCreationState = useAppSelector(state => state.userCreation);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  const postHog = usePostHog();
  const gxToken = useGxTokenFromStorage();

  // Derived state
  const loading = userCreationState.status === 'loading';
  const failed = userCreationState.status === 'failed';
  const userEmail = useMemo(
    () => decodeURIComponent(urlSearchParams.get(EMAIL_PARAM) ?? ''),
    [urlSearchParams]
  );

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    market: '',
    usage: ''
  });
  const [activeStep, setActiveStep] = useState(0);
  const [inviteRecipients, setInviteRecipients] = useState<InviteRecipient[]>([
    { email: '', id: generateSecureRandomString() }
  ]);
  const [captchaToken, setCaptchaToken] = useState('');
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  const createUserObject = useCallback(
    (): User => ({
      email: userEmail,
      hs_firstname: formData.firstName,
      hs_lastname: formData.lastName,
      gxgo_declared_data_usage: formData.usage,
      hs_company: formData.companyName,
      prefMarket: formData.market
    }),
    [userEmail, formData]
  );

  // Handle entrance logic
  const handleEntrance = useCallback(() => {
    if (gxToken) {
      navigate('/markets');
    } else if (!userEmail || !verificationToken) {
      navigate('/onboarding');
    } else {
      dispatch(getMarketsList());
    }
  }, [gxToken, userEmail, verificationToken, navigate, dispatch]);

  // Initialize component
  useEffect(() => {
    dispatch(resetStatus());
    handleEntrance();
  }, [dispatch, handleEntrance]);

  useEffect(() => {
    if (domainConfig) {
      setFormData(prevState => ({ ...prevState, companyName: domainConfig.company }));
    }
  }, [domainConfig]);

  // ReCaptcha refresh intervals
  useEffect(() => {
    const intervalId = setInterval(() => {
      setRefreshReCaptcha(prev => !prev);
    }, RECAPTCHA_REFRESH_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setRefreshReCaptcha(prev => !prev);
    }, RECAPTCHA_TIMEOUT);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [refreshReCaptcha]);

  // API calls
  const sendInvites = useCallback(async () => {
    const validRecipients = inviteRecipients.filter(recipient => recipient.email.trim().length > 0);

    if (validRecipients.length === 0) return;

    try {
      return await httpInstances.axiosGoInstance.post(
        `/share?verificationToken=${verificationToken}`,
        {
          recipient: validRecipients.map(recipient => recipient.email),
          subject: "You're invited to join GX Go!",
          template: 'go_share_gxgo_email.ftl'
        }
      );
    } catch (error) {
      console.error('Failed to send invites:', error);
      throw error;
    }
  }, [inviteRecipients, verificationToken]);

  const createNewUser = useCallback(async () => {
    const user = createUserObject();

    try {
      const response = await dispatch(
        createUser({
          user,
          captchaToken,
          verificationToken
        })
      ).unwrap();

      if (response?.data?.token && response.status === 200) {
        setGxTokenToStorage(response.data.token);
      }

      return response;
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    }
  }, [dispatch, createUserObject, captchaToken, verificationToken]);

  const captureOnboardingEvent = useCallback(() => {
    capturePostHogEvent(postHog, createUserObject, inviteRecipients);
  }, [postHog, createUserObject, inviteRecipients]);

  // Form submission
  const handleSubmit = useCallback(async () => {
    try {
      await Promise.all([sendInvites(), createNewUser()]);
      captureOnboardingEvent();

      navigate(`/markets/${formData.market}`);
    } catch (error) {
      console.error('Submission failed:', error);
    } finally {
      setRefreshReCaptcha(prev => !prev);
      window.lintrk?.('track', { conversion_id: 23598577 });
    }
  }, [sendInvites, createNewUser, captureOnboardingEvent, navigate, formData.market]);

  // ReCaptcha verification
  const onVerify = useCallback((token: string) => {
    setCaptchaToken(token);
  }, []);

  // Form data updaters
  const updateFormData = useCallback((field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Steps configuration
  const steps = useMemo(
    () => [
      {
        name: 'About User',
        component: (
          <AboutUser
            activeStep={activeStep}
            companyName={formData.companyName}
            domainConfig={domainConfig}
            firstName={formData.firstName}
            lastName={formData.lastName}
            setActiveStep={setActiveStep}
            setCompanyName={(value: string) => {
              updateFormData('companyName', value);
            }}
            setFirstName={(value: string) => {
              updateFormData('firstName', value);
            }}
            setLastName={(value: string) => {
              updateFormData('lastName', value);
            }}
          />
        )
      },
      {
        name: 'Default Market',
        component: (
          <DefaultMarket
            activeStep={activeStep}
            market={formData.market}
            setActiveStep={setActiveStep}
            setMarket={(value: string) => {
              updateFormData('market', value);
            }}
          />
        )
      },
      {
        name: 'Invite',
        component: (
          <Invite
            activeStep={activeStep}
            inviteRecipients={inviteRecipients}
            setActiveStep={setActiveStep}
            setInviteRecipients={setInviteRecipients}
          />
        )
      },
      {
        name: 'Usage',
        component: (
          <Usage
            activeStep={activeStep}
            handleSubmit={handleSubmit}
            setActiveStep={setActiveStep}
            setUsage={(value: string) => {
              updateFormData('usage', value);
            }}
            usage={formData.usage}
          />
        )
      }
    ],
    [
      activeStep,
      formData,
      updateFormData,
      inviteRecipients,
      setInviteRecipients,
      handleSubmit,
      domainConfig
    ]
  );

  useDocumentTitleRename(`GX Go | Onboarding | ${steps[activeStep].name}`);

  return {
    activeStep,
    steps,
    refreshReCaptcha,
    loading,
    failed,
    onVerify
  };
};

export default useOnboardingForm;
